import React, { useState, useEffect } from "react";
import DropOffLineChart from "./Charts/DropOffLineChart";
import DonutChart from "./Charts/DonutChart";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import config from "../../../config";

export default function FeaturePerformance() {

  const navigate = useNavigate();
  const { selectedDateRange } = useOutletContext();
  const [dropoffData, setDropoffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDropoffData();
  }, [selectedDateRange]);

  const fetchDropoffData = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      let url = `${baseUrl}/analytics/feature-performance/dropoff`;

      // Add date filters if available
      if (selectedDateRange?.start_date && selectedDateRange?.end_date) {
        url += `?start_date=${selectedDateRange.start_date}&end_date=${selectedDateRange.end_date}`;
      }

      const response = await fetch(url, { headers: config.getHeaders() });
      const result = await response.json();
      setDropoffData(result.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching dropoff data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique clients and weeks for chart
  const uniqueClients = [...new Set(dropoffData.map(item => item.organization_name))];
  const uniqueWeeks = [...new Set(dropoffData.map(item => item.week_start))].sort();

  // Prepare series data for line chart
  const series = uniqueClients.map(clientName => {
    const clientData = dropoffData.filter(item => item.organization_name === clientName);
    return {
      name: clientName,
      data: uniqueWeeks.map(week => {
        const item = clientData.find(d => d.week_start === week);
        return item ? item.avg_gap_minutes : 0;
      })
    };
  });

  const donutLabels = uniqueClients;
  const donutData = uniqueClients.map(clientName =>
    dropoffData.filter(item => item.organization_name === clientName).length
  );

  return (
    <Box sx={{ mt: 3 }}>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            width: "fit-content",
            whiteSpace: "nowrap",
          }}
        >
          Feature Drop-Off Trends
        </Typography>
      </Box>

      {/* ▪ Line Chart Section */}
      <Box sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: "10px", background: "#fff", position: "relative" }}>

        <Button
          variant="contained"
          sx={{
            position: "absolute",
            right: 20,
            top: 20,
            background: "#6A5BFF",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { background: "#5444ff" },
          }}
          onClick={() => navigate("/analytics/feature-performance/line-details", { state: { selectedDateRange } })}
        >
          View Details →
        </Button>

        <Typography sx={{ fontWeight: 500, mb: 2 }}>
          Avg. duration between sessions over time
        </Typography>

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && !loading && (
          <Typography sx={{ color: "red", py: 2 }}>
            Error loading dropoff data: {error}
          </Typography>
        )}

        {/* Chart */}
        {!loading && !error && (
          <DropOffLineChart series={series} weeks={uniqueWeeks} />
        )}
      </Box>


      {/* ▪ Donut Chart Section */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: "10px", background: "#fff", position: "relative" }}>
          
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: 20,
              top: 20,
              background: "#6A5BFF",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#5444ff" },
            }}
            onClick={() => navigate("/analytics/feature-performance/donut-details")}
          >
            View Details →
          </Button>

          <Typography sx={{ fontWeight: 500, mb: 2 }}>Feature Usage Overview</Typography>

          <DonutChart labels={donutLabels} data={donutData} title="Feature Usage Breakdown" />
        </Box>
      </Box>
    </Box>
  );
}
